import { skipToken } from "@reduxjs/toolkit/query";
import type { BrandDto, ProductDto, ProductUnitOptionDto, RetailerDto, SubBrandDto } from "../../../../contracts";
import { useListSubBrandsQuery } from "../../../../api/subBrandApi";
import { useListBrandsQuery } from "../../../../api/brandApi";
import { useListRetailersQuery } from "../../../../api/retailerApi";
import { useListProductUnitOptionsQuery, useListSubBrandProductsQuery } from "../../../../api/productApi";

export type SubBrandSetupSelection = {
    brandId: number | null;
    subBrandId: number | null;
    retailerId: number | null;
    packSizeKey: string | null;
};

type QueryStatus = {
    isFetching: boolean;
    isError: boolean;
    isSuccess: boolean;
};

type SubBrandSetupData = {
    brands: BrandDto[];
    allSubBrands: SubBrandDto[];
    filteredSubBrands: SubBrandDto[];
    retailers: RetailerDto[];
    quantityOptions: ProductUnitOptionDto[];
    quantity: ProductUnitOptionDto | null;
    products: ProductDto[];
    status: {
        brands: QueryStatus;
        subBrands: QueryStatus;
        retailers: QueryStatus;
        quantities: QueryStatus;
        products: QueryStatus;
    };
};

export const useSubBrandSetupData = ({
    brandId,
    subBrandId,
    retailerId,
    packSizeKey
}: SubBrandSetupSelection): SubBrandSetupData => {
    const brandsQuery = useListBrandsQuery({});
    const subBrandsQuery = useListSubBrandsQuery({});
    const retailersQuery = useListRetailersQuery({});

    const allSubBrands = subBrandsQuery.currentData?.items ?? [];
    const subBrands = allSubBrands.filter((subBrand: SubBrandDto): boolean => subBrand.brand_id === brandId);
    const hasValidSubBrand = subBrands.some((subBrand: SubBrandDto): boolean => subBrand.id === subBrandId);

    const quantitiesQuery = useListProductUnitOptionsQuery(
        brandId != null
        && subBrandId != null
        && retailerId != null
        && hasValidSubBrand
            ? {
                    brand_id: brandId,
                    sub_brand_id: subBrandId,
                    retailer_id: retailerId
                }
            : skipToken
    );

    const quantityOptions = quantitiesQuery.currentData?.items ?? [];
    const quantity = quantityOptions.find((option: ProductUnitOptionDto): boolean => option.pack_size_key === packSizeKey) ?? null;

    const productsQuery = useListSubBrandProductsQuery(
        brandId != null
        && subBrandId != null
        && retailerId != null
        && hasValidSubBrand
        && quantity != null
            ? {
                    brand_id: brandId,
                    sub_brand_id: subBrandId,
                    retailer_id: retailerId,
                    unit: quantity.unit,
                    unit_size: quantity.unit_size,
                    units_per_pack: quantity.units_per_pack,
                    limit: 999
                }
            : skipToken
    );

    return {
        brands: brandsQuery.currentData?.items ?? [],
        allSubBrands,
        filteredSubBrands: subBrands,
        retailers: retailersQuery.currentData?.data ?? [],
        quantityOptions,
        quantity,
        products: productsQuery.currentData?.items ?? [],
        status: {
            brands: brandsQuery,
            subBrands: subBrandsQuery,
            retailers: retailersQuery,
            quantities: quantitiesQuery,
            products: productsQuery
        }
    };
};
